import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables } from "@/integrations/supabase/types";

type Conversation = Tables<"conversations">;

interface SummarizeResponse {
  summary_markdown: string;
  model_used: string;
  last_message_id: string | null;
  input_tokens: number;
  output_tokens: number;
  message_count: number;
}

/**
 * Compacta uma conversa longa: chama a edge function `summarize-conversation`
 * para gerar um resumo estruturado da conversa original e cria uma nova
 * conversa (fork_kind='summary') contendo apenas esse resumo como ponto de
 * partida. A conversa original permanece intacta.
 */
export function useSummarizeAndFork() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conversationId }: { conversationId: string }) => {
      const { data: sessionData } = await supabase.auth.getSession();
      const accessToken = sessionData.session?.access_token;
      if (!accessToken) throw new Error("Não autenticado");

      // 1. Gera o resumo via edge function.
      const { data: summary, error: fnError } =
        await supabase.functions.invoke<SummarizeResponse>("summarize-conversation", {
          body: { conversation_id: conversationId },
        });
      if (fnError) {
        // supabase-js embrulha o corpo do erro; tentar extrair a mensagem original.
        const ctx: any = (fnError as any).context;
        let message = fnError.message;
        try {
          const body = ctx ? await ctx.json() : null;
          if (body?.error) {
            message = typeof body.error === "string" ? body.error : JSON.stringify(body.error);
          }
        } catch { /* ignore */ }
        throw new Error(message);
      }
      if (!summary) throw new Error("Resumo vazio");

      // 2. Carrega dados da conversa original para preencher metadados do fork.
      const { data: original, error: convError } = await supabase
        .from("conversations")
        .select("*")
        .eq("id", conversationId)
        .single();
      if (convError) throw convError;

      const { data: authData } = await supabase.auth.getUser();
      const userId = authData.user?.id;
      if (!userId) throw new Error("Não autenticado");

      const { data: ownerProfile } = await supabase
        .from("profiles")
        .select("nome, email")
        .eq("id", original.user_id)
        .maybeSingle();
      const ownerName = ownerProfile?.nome || ownerProfile?.email || null;

      // 3. Cria a nova conversa marcada como fork por resumo.
      const { data: forked, error: forkError } = await supabase
        .from("conversations")
        .insert({
          user_id: userId,
          title: `Resumo: ${original.title.slice(0, 80)}`,
          model: original.model,
          project_id: original.project_id,
          agent_id: original.agent_id,
          scope: (original as any).scope ?? (original.project_id ? "project" : "personal"),
          forked_from_conversation_id: conversationId,
          forked_at_message_id: summary.last_message_id,
          forked_from_title: original.title,
          forked_from_owner_name: ownerName,
          fork_kind: "summary",
        } as any)
        .select()
        .single();
      if (forkError) throw forkError;

      // 4. Insere as duas mensagens iniciais: system (instrução) + assistant (resumo).
      const systemContent =
        "Esta conversa é a continuação resumida de uma conversa anterior. O resumo estruturado abaixo substitui o histórico completo — trate-o como o contexto factual da conversa e continue a partir dele.";
      const { error: insertError } = await supabase.from("messages").insert([
        {
          conversation_id: forked.id,
          role: "system",
          content: systemContent,
        },
        {
          conversation_id: forked.id,
          role: "assistant",
          content: summary.summary_markdown,
          metadata: {
            summary_of_conversation_id: conversationId,
            summary_model_used: summary.model_used,
            summary_message_count: summary.message_count,
            usage: {
              input_tokens: summary.input_tokens,
              output_tokens: summary.output_tokens,
              total_tokens: summary.input_tokens + summary.output_tokens,
            },
          },
        },
      ] as any);
      if (insertError) throw insertError;

      return forked as Conversation;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    },
  });
}
